<template>
  <!-- Mobile Backdrop -->
  <Transition
    enter-active-class="transition-opacity duration-200 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="mobileMenuOpen"
      class="fixed inset-0 bg-gray-900 bg-opacity-50 z-30 lg:hidden"
      @click="mobileMenuOpen = false"
      aria-hidden="true"
    />
  </Transition>

  <!-- Sidebar -->
  <aside
    class="sidebar fixed left-0 top-0 h-screen bg-white border-r border-gray-200 shadow-lg transition-all duration-150 z-40 flex flex-col"
    :class="[
      isCollapsed ? 'w-16' : 'w-60',
      mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
    ]"
  >
    <!-- Logo/Brand -->
    <div 
      class="flex items-center justify-center flex-shrink-0 border-b border-gray-200 transition-all duration-150"
      :class="isCollapsed ? 'py-4 px-2' : 'py-4 px-4'"
    >
      <!-- Collapsed: Show "B" in circle -->
      <Transition
        enter-active-class="transition-opacity duration-150"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <div
          v-if="isCollapsed"
          key="collapsed"
          class="flex items-center justify-center w-10 h-10 bg-blue-500 rounded-full"
        >
          <span class="text-white text-xl font-bold">B</span>
        </div>
        <!-- Expanded: Show full "Boostpoint" text -->
        <h1
          v-else
          key="expanded"
          class="text-xl font-bold text-gray-900 whitespace-nowrap"
        >
          Boostpoint
        </h1>
      </Transition>
    </div>

    <!-- Navigation Items -->
    <nav class="flex-1 overflow-y-auto py-4">
      <template v-for="(group, groupIndex) in groupedNavItems" :key="groupIndex">
        <!-- Section Header -->
        <div
          v-if="group.section"
          class="px-3 py-2 mt-2 first:mt-0"
          :class="isCollapsed ? 'text-center' : ''"
        >
          <span 
            v-if="!isCollapsed"
            class="text-xs font-semibold text-gray-400 uppercase tracking-wider"
          >
            {{ group.section }}
          </span>
          <div 
            v-else 
            class="w-6 h-px bg-gray-300 mx-auto"
            :title="group.section"
          />
        </div>
        <!-- Nav Items in Group -->
        <SidebarNavItem
          v-for="item in group.items"
          :key="item.to"
          :to="item.to"
          :icon="item.icon"
          :label="item.label"
          :collapsed="isCollapsed"
          @click="handleNavClick"
        />
      </template>
    </nav>

    <!-- Bottom Section -->
    <div class="flex-shrink-0 border-t border-gray-200">
      <div class="p-3">
        <div
          v-if="!isCollapsed"
          class="text-xs text-gray-500 truncate mb-2"
          :title="userEmail || 'Authenticated user'"
        >
          {{ userEmail || 'Authenticated user' }}
        </div>
        <button
          @click="logout"
          class="w-full flex items-center justify-center gap-2 px-2 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-150"
          :title="isCollapsed ? 'Sign out' : undefined"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span v-if="!isCollapsed">Sign out</span>
        </button>
      </div>

      <!-- Collapse Toggle -->
      <div class="border-t border-gray-200">
        <button
          @click="toggleCollapse"
          class="w-full flex items-center justify-center py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors duration-150 hidden lg:flex"
          :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        >
          <svg 
            class="w-5 h-5 transition-transform duration-150"
            :class="{ 'rotate-180': isCollapsed }"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
    </div>
  </aside>

  <!-- Mobile Menu Button -->
  <button
    @click="mobileMenuOpen = true"
    class="fixed top-4 left-4 z-50 lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors duration-150"
    aria-label="Open menu"
  >
    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  </button>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import SidebarNavItem from './SidebarNavItem.vue';
import { beginLogoutRedirect, getAuthenticatedUserEmail } from '../auth/oidc';

// Emit collapse state changes
const emit = defineEmits<{
  'collapse-change': [isCollapsed: boolean]
}>();

// Sidebar state
const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';
const isCollapsed = ref(false);
const mobileMenuOpen = ref(false);
const userEmail = ref<string | null>(null);

// Navigation items configuration
interface NavItem {
  to: string;
  icon: string;
  label: string;
  section?: string;
}

const navItems: NavItem[] = [
  // Meta Ads Section
  {
    to: '/meta-accounts',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    label: 'Advertiser Accounts',
    section: 'Meta Ads'
  },
  {
    to: '/meta-campaign-launch',
    icon: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
    label: 'Launch Campaign',
    section: 'Meta Ads'
  },
  {
    to: '/meta-campaign-editor',
    icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    label: 'Edit Campaigns',
    section: 'Meta Ads'
  },
  {
    to: '/advertiser-budgets',
    icon: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2m-2 0V3m0 2H9m3 0h3m-6 4h6m-6 4h6m-6 4h4',
    label: 'Advertiser Budgets',
    section: 'Meta Ads'
  },
  {
    to: '/campaign-spend',
    icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z',
    label: 'Campaign Spend',
    section: 'Meta Ads'
  },
  // Recruiting Section
  {
    to: '/candidate-resumes',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    label: 'Candidate Resumes',
    section: 'Recruiting'
  },
  {
    to: '/leads',
    icon: 'M7 7h10M7 12h10M7 17h6M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z',
    label: 'Programmatic Leads',
    section: 'Recruiting'
  },
  // System Section
  {
    to: '/jobs',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
    label: 'Jobs',
    section: 'System'
  },
  {
    to: '/health',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    label: 'Health Check',
    section: 'System'
  },
  {
    to: '/llm-test',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    label: 'LLM Test',
    section: 'System'
  }
];

// Group nav items by section
const groupedNavItems = computed(() => {
  const groups: { section: string | null; items: NavItem[] }[] = [];
  let currentSection: string | null = null;
  let currentGroup: NavItem[] = [];

  for (const item of navItems) {
    const itemSection = item.section || null;
    if (itemSection !== currentSection) {
      if (currentGroup.length > 0) {
        groups.push({ section: currentSection, items: currentGroup });
      }
      currentSection = itemSection;
      currentGroup = [item];
    } else {
      currentGroup.push(item);
    }
  }
  if (currentGroup.length > 0) {
    groups.push({ section: currentSection, items: currentGroup });
  }
  return groups;
});

// Check if screen is mobile/tablet (< 1024px)
const isMobileOrTablet = () => window.innerWidth < 1024;

// Toggle collapse state
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
  emit('collapse-change', isCollapsed.value);
};

// Handle navigation click (close mobile menu)
const handleNavClick = () => {
  if (mobileMenuOpen.value) {
    mobileMenuOpen.value = false;
  }
};

const logout = async () => {
  await beginLogoutRedirect();
};

// Handle window resize
const handleResize = () => {
  if (isMobileOrTablet()) {
    // Force collapsed on mobile/tablet
    if (!isCollapsed.value) {
      isCollapsed.value = true;
      emit('collapse-change', isCollapsed.value);
    }
  }
};

// Load collapsed state from localStorage on mount
onMounted(() => {
  // Check screen size - override localStorage for mobile/tablet
  if (isMobileOrTablet()) {
    isCollapsed.value = true;
  } else {
    // Desktop: load from localStorage, default to expanded (false)
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    isCollapsed.value = saved === 'true';
  }
  
  // Emit initial state
  emit('collapse-change', isCollapsed.value);

  // Add resize listener
  window.addEventListener('resize', handleResize);

  void getAuthenticatedUserEmail().then((email) => {
    userEmail.value = email;
  });
});

// Cleanup resize listener
onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

// Save collapsed state to localStorage when it changes (desktop only)
watch(isCollapsed, (newValue) => {
  if (!isMobileOrTablet()) {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
  }
});

// Prevent body scroll when mobile menu is open
watch(mobileMenuOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});
</script>

<style scoped>
/* Ensure smooth transitions */
.sidebar {
  transition-property: width, transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Custom scrollbar for navigation */
nav::-webkit-scrollbar {
  width: 4px;
}

nav::-webkit-scrollbar-track {
  background: transparent;
}

nav::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

nav::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>


